import { CheckFatIcon } from "@/components/icons/check-fat-icon";
import { QueryKeys } from "@/constants/query-keys";
import { useCreatePlan } from "@/hooks/requests/plan/use-create-plan";
import { useMarkOnboardCompleted } from "@/hooks/requests/user/use-mark-onboard-completed";
import { useToast } from "@/hooks/ui/use-toast";
import { cn } from "@/utils/cn";
import { safeAreaBottomInset } from "@/utils/safe-area-classes";
import { IonContent, IonPage, useIonRouter } from "@ionic/react";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { setMixpanelOnboardingEvent } from "../../utils/mixpanel-onboarding";
import { setSmartlookOnboardingEvent } from "../../utils/smartlook-onboarding";

const CHECKLIST = ["No counting", "No confusion", "Just real life - and real results"];

const OnboardingMeetNutriCoachPage = () => {
  const navigation = useIonRouter();
  const queryClient = useQueryClient();
  const { showError } = useToast();
  const { markOnboardCompletedAsync, isPending: isMarking } = useMarkOnboardCompleted();
  const { updatePlanAsync, isLoading: isPlanning } = useCreatePlan();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isLoading = isMarking || isPlanning || isSubmitting;

  const handleFinalize = async () => {
    if (isLoading) return;
    setIsSubmitting(true);
    try {
      await markOnboardCompletedAsync();
      await updatePlanAsync();
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [QueryKeys.USER] }),
        queryClient.invalidateQueries({ queryKey: [QueryKeys.SUBSCRIPTION_STATUS] }),
      ]);
      setSmartlookOnboardingEvent({ type: "completed" });
      setMixpanelOnboardingEvent({ type: "completed" });
      navigation.push("/overview", "forward", "replace");
    } catch (error) {
      showError(error instanceof Error ? error.message : "An unexpected error occurred");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <IonPage>
      <IonContent fullscreen>
        <div className="relative h-full overflow-hidden bg-[#fefefe]">
          <div className="to-splash-screen-background/30 pointer-events-none absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-b from-transparent" />

          <div className="absolute inset-x-0 top-[15%] flex flex-col items-center px-9">
            <img
              src="/assets/images/onboarding-meet/nutricoach-mascot-clean.png"
              alt=""
              className="size-[110px]"
            />
            <h2 className="pt-[44px] text-center text-[22px] leading-tight font-semibold text-black">
              It's time to meet your
              <br />
              NutriCoach <span aria-hidden>💜</span>
            </h2>
          </div>

          <div className="absolute inset-x-0 top-[44.8%] flex h-11 items-center justify-center bg-[#f0e4ff]">
            <p className="font-['Alumni_Sans'] text-[30px] tracking-[1px] text-black uppercase">
              <span className="font-normal">Built to keep you </span>
              <span className="font-bold">on plan</span>
            </p>
          </div>

          <div className="absolute top-[59.3%] left-[17.8%] flex flex-col gap-2.5">
            {CHECKLIST.map((item) => (
              <div key={item} className="flex items-center gap-4">
                <CheckFatIcon className="size-7 shrink-0 fill-[#620ad5]" />
                <span style={{ fontFamily: "var(--font-sans-serif)" }} className="text-[17px] font-medium whitespace-nowrap text-[#620ad5]">
                  {item}
                </span>
              </div>
            ))}
          </div>

          <div
            className={cn(
              "absolute inset-x-0 flex justify-center px-9",
              safeAreaBottomInset("1.75rem"),
            )}
          >
            <button
              type="button"
              onClick={handleFinalize}
              disabled={isLoading}
              className="h-[50px] w-full max-w-[321px] rounded-[40px] bg-[linear-gradient(to_bottom,#513775,#43246e)] text-base font-medium text-white disabled:opacity-60"
            >
              {isLoading ? "Loading..." : "Meet NutriCoach"}
            </button>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default OnboardingMeetNutriCoachPage;
